/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { PopoverContentProps } from '@radix-ui/react-popover';
import { Root, Trigger, Portal, Content, Close } from '@radix-ui/react-popover';
import { styled, css } from 'styled-components';
import { IconButton } from '../IconButton';
import { Icon } from '../Icon';
import { getControlProps } from '../../private/helpers/getControls/getControlProps';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { PopoverArrow } from './PopoverArrow';

type StyledPopoverProps = {
  $unstyled?: boolean;
  $colorScheme: ColorSchemeTypes;
};

const StyledContent = styled(Content)<StyledPopoverProps>`
  z-index: var(--wui-zindex-popover);
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  ${({ $unstyled }) =>
    !$unstyled &&
    css`
      border-radius: var(--wui-border-radius-02);
      padding: var(--wui-space-04);
      max-width: var(--wui-popover-max-width, 320px);
      max-height: var(--wui-popover-max-height, auto);
      background-color: var(--wui-color-bg-surface);
      box-shadow: var(--wui-elevation-01);
      border: 1px solid var(--wui-color-border);
      overflow: auto;
    `}

  [data-wui-popover-close] {
    position: absolute;
    top: var(--wui-space-02);
    right: var(--wui-space-02);
  }
`;

type ControlledPopoverProps = {
  /**
   * Is the popover open.
   * Must be used with `onOpenChange`.
   */
  isOpen: boolean;

  /** When an attempt to toggle the popover is triggered.
   *  Must be used with `isOpen`.
   */
  onOpenChange: (isOpen: boolean) => void;
};

type UncontrolledPopoverProps = {
  isOpen?: never;
  onOpenChange?: never;
};

type PopoverControlProps = ControlledPopoverProps | UncontrolledPopoverProps;

type ContentProps = Omit<
  PopoverContentProps,
  | 'alignOffset'
  | 'arrowPadding'
  | 'asChild'
  | 'collisionPadding'
  | 'hideWhenDetached'
  | 'sideOffset'
  | 'updatePositionStrategy'
>;

export type PopoverProps = ContentProps &
  PopoverControlProps & {
    /**
     * The content of the popover
     */
    children: ReactNode;
    /**
     * Sets the [color scheme](/docs/color-schemes)
     */
    colorScheme?: ColorSchemeTypes;
    /**
     * The maximum width of the popover
     */
    maxWidth?: string;
    /**
     * The maximum height of the popover
     */
    maxHeight?: string;
    /**
     * Determines if the close button is visible (if false, `esc` will still close the Modal)
     */
    hideCloseButton?: boolean;
    /**
     * An interactive element. Most commonly a [Button](?path=/docs/components-button--docs) or [IconButton]([Button](?path=/docs/components-iconbutton--docs)).
     */
    trigger: JSX.Element;
    /**
     * If true, the popover will use the unstyled Content component instead of StyledContent
     */
    unstyled?: boolean;
    /**
     * If true, the popover will use a custom radix arrow to trigger
     */
    withArrow?: boolean;
    /**
     * Only works if `withArrow` is true, animates the hotspot attached to the arrow
     */
    isAnimated?: boolean;
    /**
     * @ignore
     * Inline styles applied to the popover popup
     */
    style?: CSSProperties;
  };

/**
 * Displays rich content in a portal, triggered by a button.
 */
export const Popover = ({
  children,
  trigger,
  isOpen = false,
  hideCloseButton = false,
  maxWidth = '320px',
  maxHeight = 'auto',
  onOpenChange,
  unstyled = false,
  withArrow = false,
  isAnimated = false,
  colorScheme = 'inherit',
  style,
  ...props
}: PopoverProps): JSX.Element => {
  const sideOffset = withArrow ? -24 : 8;
  const mergedStyle = {
    '--wui-popover-max-width': maxWidth,
    '--wui-popover-max-height': maxHeight,
    ...style,
  } as CSSProperties;

  return (
    <Root {...getControlProps(isOpen, onOpenChange)}>
      <Trigger asChild={true}>{trigger}</Trigger>
      <Portal>
        <StyledContent
          $colorScheme={colorScheme}
          sideOffset={sideOffset}
          {...props}
          $unstyled={unstyled}
          style={mergedStyle}
        >
          {!hideCloseButton && (
            <Close asChild={true}>
              <IconButton
                data-wui-popover-close={true}
                label="Close"
                variant="ghost"
              >
                <Icon type="close" />
              </IconButton>
            </Close>
          )}
          {withArrow ? <PopoverArrow isAnimated={isAnimated} /> : null}
          <div>{children}</div>
        </StyledContent>
      </Portal>
    </Root>
  );
};

Popover.displayName = 'Popover';
